// Helpers around the `hw_fingerprint` node pushed by Monitoring 6.0.4+ once per
// service start (mdm/devices/<MAC>/hw_fingerprint).
//
// Why it exists: a firmware image is safe only for boxes with the same BOARD
// (SoC, DDR, eMMC, WiFi module, partition table). The vendor ships several
// boards under one shell name ("X96Q"), so neither the model nor the visible
// build string identifies the hardware. `hw_hash` is computed on the box from
// hardware-only facts; equal hashes = same board = can share a firmware.
// `hw_hash_basis` carries the exact material so operators can see WHY two
// boxes differ.

/** Sentinel filter value for "device has not reported a fingerprint yet". */
export const HW_NOT_REPORTED = '.none.';

/** The fingerprint object, or null when the device never reported one. */
export function getHwFingerprint(device) {
    const fp = device?.hw_fingerprint;
    return fp && typeof fp === 'object' ? fp : null;
}

/** The 8-char group key, or null. */
export function getHwHash(device) {
    const h = getHwFingerprint(device)?.hw_hash;
    return typeof h === 'string' && h.trim() ? h.trim() : null;
}

/**
 * Short human label for a fingerprint, e.g. "cupid / sun50iw9p1 / exdroid".
 * Falls back gracefully when some identity fields are missing.
 */
export function hwShortLabel(fp) {
    if (!fp) return '';
    return [fp.platform, fp.hardware, fp.board].filter(Boolean).join(' / ');
}

// ---------------------------------------------------------------------
// Board-level grouping.
//
// hw_hash is exact: it also changes with the eMMC manufacturer, the kernel
// build date (= vendor batch) and whether the WiFi module happened to be
// enumerated at boot. That is right for "is this box byte-for-byte the same
// hardware?" but too fine for "which boxes can share a firmware?". Fleet data
// (2026-09-06) showed 27 hashes for what is really ~4 X96Q boards.
//
// boardKey keeps only what decides firmware compatibility: the device/board
// name, SoC, device tree, kernel VERSION (not date), RAM class, eMMC size and
// the partition table minus the auto-sized data partition. It is computed
// here from fields the collector already reports, so no Monitoring change is
// needed to regroup the fleet.
// ---------------------------------------------------------------------

const DATA_PARTITIONS = new Set(['UDISK', 'userdata', 'data']);

/** "4.9.170" from the kernel banner, or ''. */
export function kernelVersion(fp) {
    const m = /^Linux version (\S+)/.exec(fp?.kernel || '');
    return m ? m[1] : '';
}

/** RAM rounded to the nearest 256 MB so 1977 and 2008 both read as 2048. */
export function ramBucketMb(fp) {
    const mb = Number(fp?.ram_total_mb);
    return Number.isFinite(mb) && mb > 0 ? Math.round(mb / 256) * 256 : 0;
}

/**
 * Partition table as "name:MB,..." without the auto-sized data partition and
 * without eMMC hardware areas (mmcblk0gp0/boot0/rpmb...): those are features of
 * the eMMC chip itself, not of the image (an SK Hynix eMMC on one X96Q exposes
 * an extra mmcblk0gp0 the Toshiba/Samsung ones do not).
 */
export function layoutWithoutData(fp) {
    const p = fp?.partitions;
    if (!p || typeof p !== 'object') return '';
    return Object.keys(p)
        .filter(k => !DATA_PARTITIONS.has(k) && !k.startsWith('mmcblk'))
        .sort()
        .map(k => `${k}:${p[k]}`)
        .join(',');
}

/** Stable board identity string, or null when the fingerprint is too thin to judge. */
export function boardKey(fp) {
    if (!fp) return null;
    const parts = [
        fp.product_device || '',
        fp.platform || '',
        fp.hardware || '',
        fp.dt_compatible || '',
        kernelVersion(fp),
        String(ramBucketMb(fp)),
        fp.emmc_size_gb != null ? String(fp.emmc_size_gb) : '',
        layoutWithoutData(fp),
    ];
    // Need at least a device name or a SoC to call it a board.
    if (!parts[0] && !parts[2]) return null;
    return parts.join('|');
}

export function getBoardKey(device) {
    return boardKey(getHwFingerprint(device));
}

/** Human label for a board, e.g. "eros-p1 · sun50iw9p1 (cupid) · 2 GB / 16 GB · kernel 4.9.170 · Android 10". */
export function boardLabel(fp) {
    if (!fp) return '';
    const ram = ramBucketMb(fp);
    const ramTxt = ram ? `${ram >= 1024 ? (ram / 1024).toFixed(ram % 1024 ? 1 : 0) + ' GB' : ram + ' MB'}` : '?';
    const emmc = fp.emmc_size_gb != null ? `${fp.emmc_size_gb} GB` : '?';
    const soc = fp.hardware ? `${fp.hardware}${fp.platform ? ` (${fp.platform})` : ''}` : (fp.platform || '');
    const kv = kernelVersion(fp);
    return [
        fp.product_device || 'unknown device',
        soc,
        `${ramTxt} / ${emmc}`,
        kv ? `kernel ${kv}` : null,
        fp.android_version ? `Android ${fp.android_version}` : null,
    ].filter(Boolean).join(' · ');
}

/**
 * Group a devices map by boardKey. Each board carries the vendor batches
 * (origin_build) and exact hashes seen inside it, so an operator can see
 * "same board, 9 batches, 27 hashes" at a glance.
 *
 * @returns {{ boards: Array<{key, label, count, online, hashes: string[], batches: Array<{origin, count}>, models: string[], sampleMac}>, notReported: {count, online} }}
 */
export function summarizeBoards(devices) {
    const byKey = new Map();
    const notReported = { count: 0, online: 0 };

    Object.entries(devices || {}).forEach(([mac, dev]) => {
        const online = dev?.status === 'online';
        const fp = getHwFingerprint(dev);
        const key = boardKey(fp);
        if (!key) {
            notReported.count += 1;
            if (online) notReported.online += 1;
            return;
        }
        let b = byKey.get(key);
        if (!b) {
            b = { key, label: boardLabel(fp), count: 0, online: 0, hashes: new Set(), batches: new Map(), models: new Set(), sampleMac: mac };
            byKey.set(key, b);
        }
        b.count += 1;
        if (online) b.online += 1;
        if (fp.hw_hash) b.hashes.add(fp.hw_hash);
        const origin = fp.origin_build || fp.build_incremental || 'unknown';
        b.batches.set(origin, (b.batches.get(origin) || 0) + 1);
        const model = fp.vendor_model || dev.device_model;
        if (model) b.models.add(model);
    });

    const boards = Array.from(byKey.values())
        .map(b => ({
            ...b,
            hashes: Array.from(b.hashes).sort(),
            batches: Array.from(b.batches.entries())
                .map(([origin, count]) => ({ origin, count }))
                .sort((a, c) => c.count - a.count || a.origin.localeCompare(c.origin)),
            models: Array.from(b.models).sort(),
        }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

    return { boards, notReported };
}

/**
 * Group a devices map ({mac: device}) by hw_hash.
 *
 * @returns {{ groups: Array<{hash, count, online, platform, hardware, board, dtCompatible, originBuilds: string[], models: string[], sampleMac}>, notReported: {count, online} }}
 *   `groups` is sorted by device count, largest first.
 */
export function summarizeHwGroups(devices) {
    const byHash = new Map();
    const notReported = { count: 0, online: 0 };

    Object.entries(devices || {}).forEach(([mac, dev]) => {
        const online = dev?.status === 'online';
        const fp = getHwFingerprint(dev);
        const hash = getHwHash(dev);
        if (!fp || !hash) {
            notReported.count += 1;
            if (online) notReported.online += 1;
            return;
        }
        let g = byHash.get(hash);
        if (!g) {
            g = {
                hash,
                count: 0,
                online: 0,
                platform: fp.platform || '',
                hardware: fp.hardware || '',
                board: fp.board || '',
                dtCompatible: fp.dt_compatible || '',
                originBuilds: new Set(),
                models: new Set(),
                sampleMac: mac,
            };
            byHash.set(hash, g);
        }
        g.count += 1;
        if (online) g.online += 1;
        if (fp.origin_build) g.originBuilds.add(fp.origin_build);
        const model = fp.vendor_model || dev.device_model;
        if (model) g.models.add(model);
    });

    const groups = Array.from(byHash.values())
        .map(g => ({
            ...g,
            originBuilds: Array.from(g.originBuilds).sort(),
            models: Array.from(g.models).sort(),
        }))
        .sort((a, b) => b.count - a.count || a.hash.localeCompare(b.hash));

    return { groups, notReported };
}
